import React from 'react';
import classNames from 'classnames';
import SanitizeInnerHTML from 'components/SanitizeInnerHTML/SanitizeInnerHTML';
import { ResizableBox } from 'react-resizable';
import renderIf from 'render-if';

const NameRow = ({
  resource,
  compactView,
  scale,
  thickBars,
  fontSize,
  parentHeightByRow = 0,
  viewOnly,
  headerWidths,
  headerDepth = 0,
  updateHeaderWidths,
  persistHeaderWidths,
}) => {
  const heightOfRows = (resource, height) => {
    return resource.heightOfRows(
      compactView,
      height,
      scale.minDate,
      scale.maxDate,
      thickBars,
      fontSize
    );
  };

  const heightOfChildren = (resource, parentHeightByRow) => {
    const heightByRow = Math.max(
      resource.height / (resource.children || []).length,
      parentHeightByRow
    );

    const heightOfRowsTotal = resource.children.reduce((sum, child) => {
      return sum + heightOfRows(child, heightByRow);
    }, 0);

    return Math.max(resource.height, heightOfRowsTotal, parentHeightByRow);
  };

  const calculateRowHeight = resource => {
    if (resource.isLastColumn) {
      return Math.max(
        parentHeightByRow,
        heightOfRows(resource, parentHeightByRow)
      );
    } else if (resource.children?.length > 0) {
      return heightOfChildren(resource, parentHeightByRow);
    } else {
      return 0;
    }
  };

  const calculateParentHeightByRow = resource => {
    return Math.max(
      resource.height / (resource.children || []).length,
      parentHeightByRow
    );
  };

  const childRows = (resource, height) => {
    if (resource.children?.length > 0) {
      return (
        <div className='children'>
          {resource.children.map((child, index) => (
            <NameRow
              key={`NameRow-${child.unique_key}-${index}`}
              viewOnly={viewOnly}
              scale={scale}
              resource={child}
              compactView={compactView}
              thickBars={thickBars}
              fontSize={fontSize}
              parentHeightByRow={height}
              headerWidths={headerWidths}
              headerDepth={headerDepth + 1}
              updateHeaderWidths={updateHeaderWidths}
              persistHeaderWidths={persistHeaderWidths}
            />
          ))}
        </div>
      );
    }
  };

  const parentHeight = calculateParentHeightByRow(resource);
  const height = calculateRowHeight(resource);

  const className = classNames('timeline-row', {
    'timeline-row__last-column': resource.isLastColumn,
    'timeline-row__parent-column': !resource.isLastColumn,
  });

  const handleResize = (index, newWidth) => {
    const newWidths = [...headerWidths];
    newWidths[index] = newWidth;
    updateHeaderWidths(newWidths);
  };

  const handleResizePersist = (index, newWidth) => {
    const newWidths = [...headerWidths];
    newWidths[index] = newWidth;
    persistHeaderWidths(newWidths);
  };

  const contentWrapperStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    maxHeight: height,
  };

  return (
    <div
      key={`NameRowInner-${resource.unique_key}`}
      className={className}
      style={
        resource.isLastColumn
          ? { height, maxHeight: height }
          : { maxHeight: height }
      }
      data-resource-key={resource.unique_key}
    >
      <div
        className='timeline-row-name-wrapper'
        data-color-by={resource.color}
        style={{
          width: headerWidths[headerDepth],
        }}
      >
        {/* Any changes here must be reflected in the height calculation in resource.js */}

        {renderIf(window.featureFlags.a_16745_custom_timeline_resizeable)(
          <ResizableBox
            axis='x'
            handle={
              <div
                className='react-resizable-handle'
                draggable
                onDragStart={e => e.preventDefault()}
                style={{
                  left: headerWidths.length === headerDepth + 1 ? 3 : 5,
                }}
              />
            }
            onResize={(e, data) => handleResize(headerDepth, data.size.width)}
            resizeHandles={['e']}
            width={headerWidths[headerDepth]}
            onResizeStop={(e, data) =>
              handleResizePersist(headerDepth, data.size.width)
            }
          >
            <SanitizeInnerHTML
              html={resource.label}
              element={<span className='name' style={contentWrapperStyle} />}
            />
          </ResizableBox>
        )}
        {renderIf(!window.featureFlags.a_16745_custom_timeline_resizeable)(
          <div
            className='react-resizable'
            style={{
              left: headerWidths.length === headerDepth + 1 ? 3 : 5,
            }}
          >
            <SanitizeInnerHTML
              html={resource.label}
              element={<span className='name' style={contentWrapperStyle} />}
            />
          </div>
        )}
      </div>
      {childRows(resource, parentHeight)}
    </div>
  );
};

export default NameRow;
